/**
 * Driver-roster widgets (#1805 slice A2).
 *
 * The four `fields[].render` callbacks the drivers list carried, moved to their
 * only v2 carrier. Three of them read the RECORD rather than the cell value —
 * the display-name cell composes `display_name`/`name`/`id`/`onfleet_id`, the
 * duty cell shows the TASK COUNT under a duty subtitle, and the task cell
 * counts an array — which is exactly why they were callbacks. Each therefore
 * carries `csvText: stringifyRaw(value)`, the text the bespoke-renderer branch
 * of `formatFieldTextForExport` already produced, so the export does not move.
 *
 * They translate through the i18n singleton rather than `useTranslation`
 * because the callbacks they replace did: both resolve against the same live
 * instance, but only the literal copy guarantees byte-identical markup, which
 * is what `__tests__/driverResource.v2-parity.test.tsx` compares.
 *
 * No `@services/*` import — `driverAPI` stays in `./schema.tsx`.
 * `src/features/widgets.ts` is evaluated by `src/test-setup.ts` for EVERY
 * suite, ahead of any `vi.mock`, so a service reached from a widget module is
 * instantiated against the real `@lib/api` before a test can replace it (that
 * cost 19 red tests in slice A1).
 */
import i18n from '@/i18n';
import {
  stringifyRaw,
  EntityLink,
  ExternalServiceLink,
  CopyButton,
  LIST_TRUNCATE,
} from '@resource-kit/ui/react';
import { registerWidget, shortenId } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import { formatDateTimeCompact } from '@lib/utils';
import { EXTERNAL_URLS } from '@/constants/config';
import type { OnfleetWorker } from '@/types/onfleet';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    driverDisplayName: { types: 'text'; modes: 'list' };
    driverTaskSummary: { types: 'array'; modes: 'list' };
    driverDutySummary: { types: 'boolean'; modes: 'list' };
    driverLastActivity: { types: 'datetime'; modes: 'list' };
  }
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The worker behind the cell.
 *
 * Written as a widening cast rather than a `failHard`, because the callbacks
 * these replace read `row.display_name` / `row.tasks?.length` on whatever they
 * were handed and degraded rather than threw. Every read below is optional, so
 * a partial row still renders the same markup it always did.
 */
function workerOf(record: unknown): Partial<OnfleetWorker> {
  return typeof record === 'object' && record !== null ? (record as Partial<OnfleetWorker>) : {};
}

function DriverDisplayName({ record }: WidgetProps) {
  const row = workerOf(record);
  const name = row.display_name || row.name;
  const displayName = name || i18n.t('driver.unknownDriver');
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <EntityLink to={`/drivers/${row.id}`} className="text-sm font-medium">
            {displayName}
          </EntityLink>
          {name && <CopyButton value={name} />}
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {/* Driver ids are UUIDs: the shared shortener keeps the
                        trailing group, so this reads identically to the
                        driver select and to any `IdCell` (#1978). */}
          <span>ID: {row.id ? shortenId(row.id, LIST_TRUNCATE).text : 'N/A'}</span>
          {row.onfleet_id && (
            <ExternalServiceLink
              href={EXTERNAL_URLS.onfleetWorker(row.onfleet_id)}
              ariaLabel={i18n.t('ui.openInOnfleet')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function DriverTaskSummary({ record }: WidgetProps) {
  const row = workerOf(record);
  return (
    <div className="text-sm text-foreground">
      {row.tasks?.length
        ? i18n.t('driver.taskCount', { count: row.tasks.length })
        : i18n.t('driver.noTasks')}
    </div>
  );
}

/**
 * The duty column, which is the reason `is_on_duty` is `filterable: false` on
 * the model: the headline is the ACTIVE TASK COUNT and duty is only the
 * subtitle, so a boolean filter generated off the field would not match what
 * the operator reads here.
 */
function DriverDutySummary({ record }: WidgetProps) {
  const row = workerOf(record);
  return (
    <div className="text-sm">
      <div className="text-foreground font-medium">
        {i18n.t('driver.taskCount', { count: row.tasks?.length || 0 })}
      </div>
      <div className="text-xs text-muted-foreground">
        {i18n.t('driver.status', {
          status: row.is_on_duty ? i18n.t('driver.onDuty') : i18n.t('driver.offDuty'),
        })}
      </div>
    </div>
  );
}

/**
 * Live Onfleet enrichment (#driver-last-activity): `time_last_seen` is the
 * worker's `timeLastSeen` (ms epoch), or null when Onfleet has no reading / the
 * roster fetch degraded. Rendered as a compact datetime, which is likewise why
 * the model marks the field `filterable: false` — a numeric filter over the raw
 * epoch would not match the displayed value.
 */
function DriverLastActivity({ record }: WidgetProps) {
  const time = workerOf(record).time_last_seen;
  return <div className="text-sm text-foreground">{time ? formatDateTimeCompact(time) : '-'}</div>;
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerDriverWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('driverDisplayName', {
    display: DriverDisplayName,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
  registerWidget('driverTaskSummary', {
    display: DriverTaskSummary,
    csvText: rawCsv,
    supportedTypes: ['array'],
    modes: ['list'],
  });
  registerWidget('driverDutySummary', {
    display: DriverDutySummary,
    csvText: rawCsv,
    supportedTypes: ['boolean'],
    modes: ['list'],
  });
  registerWidget('driverLastActivity', {
    display: DriverLastActivity,
    csvText: rawCsv,
    supportedTypes: ['datetime'],
    modes: ['list'],
  });
}
